import { useCallback, useMemo } from 'react';
import { useExtendedBooking } from './useExtendedBooking';
import { useOptimisticBookingState } from './useOptimisticBookingState';
import { Reservation, ReservationStatus } from '@core/models';
import moment from 'moment';

export const getReservationQueue = (
  reservations: Reservation<any>[],
  openHour: number,
  closedHour: number
) => {
  // 2D array MxN with M is the total number of working hours
  // and N is the max reservations of the hour
  const queue: Reservation<any>[][] = [];
  for (let i = 0; i < closedHour - openHour; i++) {
    queue.push([]);
  }

  for (const reservation of reservations) {
    const reservedHour = moment(reservation.reserved_date).hours();
    if (reservedHour < openHour || reservedHour > closedHour) continue;
    queue[reservedHour - openHour].push(reservation);
  }
  return queue;
};

export const useReservationTimelineParams = () => {
  const { selectedDayAllRestaurantReservations } = useExtendedBooking();
  const { bookingSetting } = useOptimisticBookingState();

  const selectedDayReservationQueue = useMemo((): Reservation<any>[][] => {
    return getReservationQueue(
      selectedDayAllRestaurantReservations,
      bookingSetting.open_hour,
      bookingSetting.closed_hour
    );
  }, [selectedDayAllRestaurantReservations, bookingSetting]);

  const getTotalNumberOfPeople = useCallback(
    (hours: number) => {
      const reservations = selectedDayReservationQueue[hours - bookingSetting.open_hour];
      const inProgressReservations = reservations.filter(
        reservation => reservation.status === ReservationStatus.InProgress
      );
      if (inProgressReservations.length == 0) return 0;
      return inProgressReservations
        .map(reservation => reservation.numberOfPeople)
        .reduce((a, b) => a + b);
    },
    [selectedDayReservationQueue]
  );

  const getHourCapacityPercentage = useCallback(
    (hours: number) => {
      return (getTotalNumberOfPeople(hours) / bookingSetting.max_number_of_people || 1) * 100;
    },
    [bookingSetting, selectedDayReservationQueue]
  );

  return {
    getHourCapacityPercentage,
    selectedDayReservationQueue,
  };
};
